import { html, type TemplateResult } from "lit-html";
import { store, type AppState } from "../data/store";

/**
 * MobilePreviewCube — iPhone 17 Pro at 1:1, lives in the desktop
 * shell's right rail (NOT in Gridstack).
 *
 * Phase 2.O moved this out of the Gridstack canvas. The canvas's
 * 12-col layout always made the preview cube wider than the phone
 * (4 cols ≈ 600px vs phone's 420px), so there was ~180px of wasted
 * black space around the phone. The rail pattern matches what
 * the-kitchen-console does in RightRail.ts: rail width = phone +
 * minimal chrome, no horizontal slack.
 *
 * Collapse state is in the store (state.previewCollapsed). The
 * shell's CSS reads it via a class to size the rail (full width vs
 * narrow strip). This component only renders the chrome.
 */

const PREVIEW_WIDTH  = 402;
const PREVIEW_HEIGHT = 874;

export function renderMobilePreviewCube(state: AppState): TemplateResult {
  const collapsed = state.previewCollapsed;
  return html`
    <div class="cube cube--preview ${collapsed ? "cube--collapsed" : ""}">
      <div class="cube__head">
        <span class="cube__title">Mobile preview</span>
        ${collapsed ? "" : html`
          <span class="cube__count">iPhone 17 Pro · 1:1</span>
        `}
        <div class="cube__head-tabs mpv__controls">
          <button class="mpv__btn"
                  type="button"
                  aria-label=${collapsed ? "Expand preview" : "Collapse preview"}
                  title=${collapsed ? "Expand preview" : "Collapse preview"}
                  @click=${() => store.togglePreviewCollapsed()}>
            ${collapsed ? chevronLeft() : chevronRight()}
          </button>
          ${collapsed ? "" : html`
            <a class="mpv__btn mpv__btn--link"
               href="/m/" target="_blank" rel="noopener"
               title="Open mobile view in new tab">↗</a>
          `}
        </div>
      </div>
      ${collapsed ? "" : html`
        <div class="cube__body mpv__stage">
          <div class="mpv__phone">
            <div class="mpv__notch"></div>
            <div class="mpv__screen"
                 style="width:${PREVIEW_WIDTH}px; height:${PREVIEW_HEIGHT}px;">
              <iframe
                class="mpv__iframe"
                src="/m/"
                style="width:${PREVIEW_WIDTH}px; height:${PREVIEW_HEIGHT}px;"
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                loading="lazy"
                title="festival-ops mobile preview"
              ></iframe>
            </div>
          </div>
        </div>
      `}
    </div>
  `;
}

function chevronRight(): TemplateResult {
  return html`
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="2" stroke-linecap="round"
         stroke-linejoin="round" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  `;
}

function chevronLeft(): TemplateResult {
  return html`
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="2" stroke-linecap="round"
         stroke-linejoin="round" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  `;
}
